import { useEffect, useRef, useState } from 'react';
import { QuickTask } from '@/types/quickTask';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface QuickTaskItemProps {
  task: QuickTask;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdate: (id: string, text: string) => void;
}

export function QuickTaskItem({ task, onToggle, onDelete, onUpdate }: QuickTaskItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(task.text);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) {
      setEditText(task.text);
      return;
    }

    const timer = window.setTimeout(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [isEditing, task.text]);

  const startEditing = () => {
    setEditText(task.text);
    setIsEditing(true);
  };

  const saveEditing = () => {
    const trimmed = editText.trim();
    if (trimmed && trimmed !== task.text) {
      onUpdate(task.id, trimmed);
    }
    setIsEditing(false);
  };

  const cancelEditing = () => {
    setEditText(task.text);
    setIsEditing(false);
  };

  return (
    <div className="group flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
      <Checkbox
        checked={task.completed}
        onCheckedChange={() => onToggle(task.id)}
        className="shrink-0"
        aria-label={task.completed ? `Mark ${task.text} incomplete` : `Mark ${task.text} complete`}
      />
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <Input
            ref={inputRef}
            value={editText}
            onChange={(event) => setEditText(event.target.value)}
            onBlur={saveEditing}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                saveEditing();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                cancelEditing();
              }
            }}
            className="h-11 w-full text-base"
            style={{ fontSize: '16px' }}
            enterKeyHint="done"
            aria-label={`Edit to-do: ${task.text}`}
          />
        ) : (
          <button
            type="button"
            onClick={startEditing}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                startEditing();
              }
            }}
            className={cn(
              "block w-full min-h-11 rounded-md px-1 py-2 text-left text-sm transition-all hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              task.completed && "text-muted-foreground line-through"
            )}
            style={{ touchAction: 'manipulation' }}
            aria-label={`Edit to-do: ${task.text}`}
            title="Tap to edit"
          >
            <span className="break-words">{task.text}</span>
            {task.carriedForward && (
              <span className="ml-2 inline-flex text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                carried over
              </span>
            )}
          </button>
        )}
      </div>
      <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-primary hover:text-primary"
          onClick={startEditing}
          title="Edit"
          aria-label={`Edit ${task.text}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={() => onDelete(task.id)}
          title="Delete"
          aria-label={`Delete ${task.text}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
